(() => {
  const plainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  const removedFilters = ['filter.p.product_type', 'filter.v.option.color', 'filter.v.option.colour', 'filter.v.price.gte', 'filter.v.price.lte'];
  const cleanCollectionUrl = (current) => {
    const url = new URL(current);
    removedFilters.forEach((name) => url.searchParams.delete(name));
    if (['most-relevant', 'created-ascending', 'created-descending'].includes(url.searchParams.get('sort_by'))) {
      url.searchParams.set('sort_by', 'manual');
    }
    return url;
  };

  // Retain preview, search and other unrelated parameters, including duplicates.
  // FormData owns only filters and sorting; a new selection returns to page one.
  const collectionUrl = (current, form) => {
    const url = new URL(form.action, current);
    url.search = new URL(current).search;
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || /^page(?:_|$)/.test(key)) url.searchParams.delete(key);
    }
    const values = new FormData(form);
    const names = new Set([...values.keys()]);
    names.forEach((name) => url.searchParams.delete(name));
    for (const [name, value] of values) {
      if (typeof value === 'string' && value.trim() !== '') url.searchParams.append(name, value);
    }
    return url;
  };

  class CCProductCard extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        document.addEventListener('llcc:cart:updated', this.onCartUpdated);
        return;
      }
      this.initialised = true;
      this.enableFastTrack();
      const visual = this.querySelector('.cc-product-card__visual');
      visual?.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) return;
        const panel = this.querySelector('[data-fast-track]');
        if (panel) panel.open = true;
      });
      visual?.addEventListener('pointerleave', () => {
        const panel = this.querySelector('[data-fast-track]');
        if (panel && !panel.contains(document.activeElement) && !this.adding) panel.open = false;
      });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        const panel = this.querySelector('[data-fast-track]');
        if (panel && !panel.contains(document.activeElement) && !visual?.matches(':hover') && !this.adding) panel.open = false;
      }));
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const panel = event.target.closest('[data-fast-track]');
        if (!panel?.open) return;
        event.preventDefault();
        panel.open = false;
        panel.querySelector('summary').focus({ preventScroll: true });
      });
      this.onCartUpdated = () => {
        if (this.adding || !this.unresolvedAddition) return;
        this.unresolvedAddition = false;
        this.setFastBusy(false);
        this.querySelector('[data-fast-error]')?.setAttribute('hidden', '');
      };
      document.addEventListener('llcc:cart:updated', this.onCartUpdated);
      this.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-fast-variant]');
        if (addButton) {
          event.preventDefault();
          this.addFastTrack(addButton);
          return;
        }
        const choice = event.target.closest('[data-card-choice]');
        if (!choice || !plainClick(event)) return;
        const template = this.querySelector(`template[data-choice-template="${choice.dataset.cardChoice}"]`);
        if (!template) return;
        event.preventDefault();
        if (this.adding || this.unresolvedAddition) return;
        const image = template.content.querySelector('[data-choice-image]');
        const price = template.content.querySelector('[data-choice-price]');
        this.querySelector('[data-card-image]').replaceChildren(...image.cloneNode(true).childNodes);
        this.querySelector('[data-card-price]').replaceChildren(...price.cloneNode(true).childNodes);
        const fastTrack = template.content.querySelector('[data-choice-fast-track]');
        this.querySelector('[data-card-fast-track]').replaceChildren(...fastTrack.cloneNode(true).childNodes);
        const badges = template.content.querySelector('[data-choice-badges]');
        this.querySelector('[data-card-badges]').replaceChildren(...badges.cloneNode(true).childNodes);
        this.enableFastTrack();
        this.querySelectorAll('[data-product-link]').forEach((link) => { link.href = template.dataset.choiceUrl; });
        this.querySelectorAll('[data-card-choice]').forEach((link) => link.setAttribute('aria-current', String(link === choice)));
        this.querySelector('[data-card-colour]').textContent = template.dataset.choiceColour;
        const available = template.dataset.choiceAvailable === 'true';
        this.querySelector('[data-card-availability]').hidden = available;
        this.querySelector('[data-card-status]').textContent = `${template.dataset.choiceColour} selected.${available ? '' : ' Sold out in all sizes.'}`;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('llcc:cart:updated', this.onCartUpdated);
    }

    enableFastTrack() {
      const panel = this.querySelector('[data-fast-track]');
      if (panel) panel.hidden = false;
      this.setFastBusy(false);
    }

    setFastBusy(busy) {
      const panel = this.querySelector('[data-fast-track]');
      if (!panel) return;
      panel.setAttribute('aria-busy', String(busy));
      panel.querySelectorAll('[data-fast-variant]').forEach((button) => {
        button.disabled = busy || this.unresolvedAddition || button.hasAttribute('data-unavailable');
      });
    }

    async addFastTrack(button) {
      if (this.adding || this.unresolvedAddition || button.disabled || !button.dataset.fastVariant) return;
      const panel = button.closest('[data-fast-track]');
      const status = panel.querySelector('[data-fast-status]');
      const error = panel.querySelector('[data-fast-error]');
      if (!window.LLCC?.cart) {
        error.querySelector('[data-fast-error-text]').textContent = panel.dataset.messageError;
        error.hidden = false;
        return;
      }
      this.adding = true;
      panel.open = true;
      error.hidden = true;
      status.textContent = panel.dataset.messageAdding;
      this.setFastBusy(true);
      const payload = new FormData();
      payload.set('id', button.dataset.fastVariant);
      payload.set('quantity', '1');
      try {
        await window.LLCC.cart.add(payload, { opener: panel.querySelector('summary') });
        status.textContent = panel.dataset.messageAdded;
      } catch (failure) {
        this.unresolvedAddition = Boolean(failure.uncertain || failure.committed);
        const message = failure.code === 'inventory' ? panel.dataset.messageInventory : failure.committed ? panel.dataset.messageCommitted : failure.uncertain ? panel.dataset.messageUncertain : panel.dataset.messageError;
        error.querySelector('[data-fast-error-text]').textContent = message;
        error.hidden = false;
        status.textContent = '';
      } finally {
        this.adding = false;
        this.setFastBusy(false);
      }
    }
  }

  class CCCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        window.addEventListener('popstate', this.onPopState);
        return;
      }
      this.initialised = true;
      this.enhance();
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-collection-form]')) return;
        event.preventDefault();
        this.submitFilters();
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-collection-sort], .cc-filter input[type="checkbox"]')) this.submitFilters();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.onPopState = () => this.load(cleanCollectionUrl(window.location.href), { history: 'replace' });
      window.addEventListener('popstate', this.onPopState);
      const url = cleanCollectionUrl(window.location.href);
      if (url.href !== window.location.href) this.load(url, { history: 'replace' });
    }

    disconnectedCallback() {
      this.request?.abort();
      window.removeEventListener('popstate', this.onPopState);
    }

    enhance() {
      this.dataset.enhanced = '';
      const toggle = this.querySelector('[data-filter-toggle]');
      if (toggle) {
        toggle.hidden = false;
        toggle.setAttribute('aria-expanded', String(this.hasAttribute('data-filters-open')));
      }
      const current = new URL(window.location.href);
      const sort = this.querySelector('[data-collection-sort]').value;
      this.querySelectorAll('[data-collection-navigation]').forEach((link) => {
        const url = new URL(link.href);
        for (const key of new Set(current.searchParams.keys())) {
          if (key.startsWith('filter.') || /^page(?:_|$)/.test(key) || ['q', 'sort_by', 'section_id'].includes(key)) continue;
          if (!url.searchParams.has(key)) current.searchParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
        }
        url.searchParams.set('sort_by', sort);
        link.href = url;
      });
    }

    submitFilters() {
      const form = this.querySelector('[data-collection-form]');
      if (form.reportValidity()) this.load(collectionUrl(window.location.href, form));
    }

    onClick(event) {
      const toggle = event.target.closest('[data-filter-toggle]');
      if (toggle) {
        const opened = this.toggleAttribute('data-filters-open');
        toggle.setAttribute('aria-expanded', String(opened));
        return;
      }
      if (event.target.closest('[data-collection-retry]')) {
        if (this.failedUrl) this.load(this.failedUrl);
        return;
      }
      const link = event.target.closest('[data-collection-link], [data-clear-filters]');
      if (!link || !plainClick(event)) return;
      event.preventDefault();
      const url = new URL(link.href);
      if (link.hasAttribute('data-clear-filters')) {
        url.search = window.location.search;
        for (const key of [...url.searchParams.keys()]) {
          if (key.startsWith('filter.') || /^page(?:_|$)/.test(key)) url.searchParams.delete(key);
        }
      } else {
        // Shopify supplies filter-removal and pagination URLs. Carry through
        // unrelated preview/search parameters if its URL omits them.
        const current = new URL(window.location.href);
        for (const key of new Set(current.searchParams.keys())) {
          if (key.startsWith('filter.') || /^page(?:_|$)/.test(key) || url.searchParams.has(key)) continue;
          current.searchParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
        }
      }
      this.load(url, { scroll: Boolean(link.closest('.cc-pagination')) });
    }

    async load(url, options = {}) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const results = this.querySelector('[data-collection-results]');
      results.setAttribute('aria-busy', 'true');
      this.querySelector('[data-collection-status]').textContent = 'Updating products…';
      this.querySelector('[data-collection-error]').hidden = true;
      const endpoint = new URL(url);
      endpoint.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(endpoint, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('cc-collection');
        if (!next || next.dataset.sectionId !== this.dataset.sectionId) throw new Error('Collection response was incomplete');
        if (this.request !== request) return;
        const focused = this.contains(document.activeElement) ? document.activeElement : null;
        const focusId = focused?.id;
        const focusHref = focused?.getAttribute('href');
        const openGroups = new Set([...this.querySelectorAll('.cc-filter[open]')].map((group) => group.id));
        this.innerHTML = next.innerHTML;
        this.dataset.productCount = next.dataset.productCount;
        this.querySelectorAll('.cc-filter').forEach((group) => { group.open = openGroups.has(group.id); });
        if (options.history === 'replace') window.history.replaceState({}, '', url);
        else if (options.history !== false) window.history.pushState({}, '', url);
        this.enhance();
        this.failedUrl = null;
        const countLabel = next.querySelector('[data-results-count]').textContent.trim();
        this.querySelector('[data-collection-status]').textContent = `${countLabel}.`;
        if (focused) {
          const target = (focusId && this.querySelector(`#${CSS.escape(focusId)}`)) || (focusHref && this.querySelector(`[href="${CSS.escape(focusHref)}"]`)) || this.querySelector('[data-results-count]');
          if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
        if (options.scroll) this.scrollIntoView({ block: 'start', behavior: 'auto' });
      } catch (error) {
        if (error.name === 'AbortError' || this.request !== request) return;
        this.failedUrl = new URL(url);
        this.querySelector('[data-collection-status]').textContent = '';
        this.querySelector('[data-collection-error]').hidden = false;
        this.querySelector('[data-collection-reload]').href = url;
      } finally {
        if (this.request === request) this.querySelector('[data-collection-results]')?.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('cc-product-card')) customElements.define('cc-product-card', CCProductCard);
  if (!customElements.get('cc-collection')) customElements.define('cc-collection', CCCollection);
})();
